/**
 * The one way this app tells the agent how a request it made turned out.
 *
 * The worker fast-paths any `lk.chat` text that starts with `[api_result]`
 * (`_is_api_report`, `livekit_worker.py:83-100`): no debounce, no interrupt,
 * straight into a new LLM turn that the agent then speaks. Two things follow.
 * The grammar has to be exact — the worker also refreshes the stored
 * recommendation id only when the FIRST line contains `success`, which is why a
 * queued job says `success — queued` rather than `queued`. And the text has to
 * be small: every report is a billed, spoken turn, and its JSON is pasted into
 * the prompt verbatim.
 *
 *   [api_result] <op> success[ — <detail>]
 *   <compact JSON>                                   (optional, second line)
 *   [api_result] <op> success — queued[; <detail>]
 *   [api_result] <op> failed: <reason>
 *
 * Pure: no React, no DOM, no store — importable from Node check scripts.
 */

const MARK = '[api_result]';

/** Longest `detail` / `error` kept on the status line. */
const MAX_LINE_TEXT = 300;

/** Longest op name; an op is an identifier, anything longer is a caller bug. */
const MAX_OP = 80;

/** Strings inside the JSON are clipped to this before keys are dropped. */
const MAX_JSON_STRING = 480;

/** Hard stop for the shedding loop, so a pathological payload cannot spin. */
const MAX_SHED_PASSES = 60;

const cut = (s, max) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

/**
 * One line of plain text.
 *
 * The status line is parsed by the worker and by the agent as ONE line; a
 * newline inside `detail` would push the rest of it into what the agent reads
 * as the JSON block. Errors arrive as strings, `Error`s and GraphQL error
 * objects, so each is reduced to its message first.
 */
function oneLine(value, max) {
  let s = value;
  if (s && typeof s === 'object') {
    s = [s.message, s.error, s.reason].find((v) => typeof v === 'string') ?? '';
  }
  if (s === null || s === undefined) return '';
  return cut(String(s).replace(/[\r\n\u2028\u2029]+/g, ' ').replace(/\s+/g, ' ').trim(), max);
}

/**
 * The report text for one request.
 *
 * `ok` decides the branch; when it is omitted, the presence of `error` does.
 * `queued` marks a job that was accepted but has not finished (reviews,
 * backtests, stock reports) — the agent must not tell the user it is done.
 * `data` is attached only to a success, through `compactForAgent`, and is
 * silently left off when it cannot be made into small valid JSON. A list is
 * sent as `{ rows: [...] }`: the worker strips spaces, brackets and quotes from
 * both ends of every message before the model reads it, so JSON ending in `]`
 * would arrive without its closing bracket, while one ending in `}` arrives whole.
 */
export function formatApiResult({ op, ok, detail, data, error, queued } = {}) {
  const name = oneLine(op, MAX_OP) || 'request';
  const success = ok === undefined ? !error : !!ok;

  if (!success) {
    const reason = oneLine(error, MAX_LINE_TEXT) || oneLine(detail, MAX_LINE_TEXT) || 'unknown error';
    return `${MARK} ${name} failed: ${reason}`;
  }

  const note = oneLine(detail, MAX_LINE_TEXT);
  let line;
  if (queued) line = `${MARK} ${name} success — queued${note ? `; ${note}` : ''}`;
  else line = `${MARK} ${name} success${note ? ` — ${note}` : ''}`;

  let json = data === undefined || data === null ? '' : compactForAgent(Array.isArray(data) ? { rows: data } : data);
  /* A stored run's handle is sent even when the rest could not be: the worker
     drops the report from history, so a lost urlId cannot be asked for later. */
  if (!json && typeof data?.urlId === 'string' && data.urlId) json = JSON.stringify({ urlId: data.urlId });
  return json ? `${line}\n${json}` : line;
}

/** True for text that is a report rather than something the user said. */
export function isApiReport(text) {
  /* Same test the worker applies: leading brackets, quotes and spaces are
     ignored, and `[api_error]` counts too. */
  return typeof text === 'string' && /^[\s[\]'"]*api_(result|error)\]/i.test(text);
}

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);

/** Four decimal places: enough for a weight or a return, and far fewer tokens. */
const round = (n) => {
  if (!Number.isFinite(n)) return null;
  if (Number.isInteger(n) || Math.abs(n) >= 1e11) return n;
  const r = Math.round(n * 1e4) / 1e4;
  return Object.is(r, -0) ? 0 : r;
};

/**
 * JSON-shaped copy with what the agent never needs taken out.
 *
 * Apollo's `__typename` is on every object a query returns and says nothing
 * about the numbers; functions and `undefined` are not JSON. Class instances
 * with `toJSON` (dates) are reduced the way `JSON.stringify` would. A cycle is
 * cut rather than thrown on.
 */
function prune(value, ancestors) {
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (typeof value === 'number') return round(value);
  if (typeof value === 'bigint') return String(value);
  if (value === null || typeof value !== 'object') return value;
  if (ancestors.has(value)) return undefined;
  if (!Array.isArray(value) && !isPlainObject(value) && typeof value.toJSON === 'function') {
    return prune(value.toJSON(), ancestors);
  }
  ancestors.add(value);
  let out;
  if (Array.isArray(value)) {
    out = [];
    for (const item of value) {
      const p = prune(item, ancestors);
      out.push(p === undefined ? null : p);
    }
  } else {
    out = {};
    for (const key of Object.keys(value)) {
      if (key === '__typename') continue;
      const p = prune(value[key], ancestors);
      if (p !== undefined) out[key] = p;
    }
  }
  ancestors.delete(value);
  return out;
}

/** Evenly spaced rows that always include the first and the last. */
function sampleRows(arr, count) {
  if (arr.length <= count) return arr;
  if (count <= 1) return [arr[0]];
  const step = (arr.length - 1) / (count - 1);
  const out = [];
  for (let i = 0; i < count; i++) out.push(arr[Math.round(i * step)]);
  return out;
}

/**
 * Long arrays cut to `rows` evenly spaced entries, at every depth.
 *
 * The marker is appended rather than implied: a thinned series that looked
 * complete would be quoted by the agent as complete.
 */
function downsample(value, rows) {
  if (Array.isArray(value)) {
    const sampled = sampleRows(value, rows).map((v) => downsample(v, rows));
    if (sampled.length < value.length) sampled.push({ _sampled: `${sampled.length} of ${value.length} rows` });
    return sampled;
  }
  if (isPlainObject(value)) {
    const out = {};
    for (const key of Object.keys(value)) out[key] = downsample(value[key], rows);
    return out;
  }
  return value;
}

/** Every string clipped to `max` characters. */
function clipStrings(value, max) {
  if (typeof value === 'string') return cut(value, max);
  if (Array.isArray(value)) return value.map((v) => clipStrings(v, max));
  if (isPlainObject(value)) {
    const out = {};
    for (const key of Object.keys(value)) out[key] = clipStrings(value[key], max);
    return out;
  }
  return value;
}

const size = (v) => JSON.stringify(v).length;

/** Markers explaining a cut; removing one would hide that data is missing. */
const isMarker = (v) => isPlainObject(v) && Object.keys(v).length === 1 && '_sampled' in v;
const MARKER_KEYS = ['_omitted', '_sampled'];

/** Names listed under `_omitted` before the rest are only counted. */
const MAX_OMITTED_NAMES = 12;

function memberCount(v) {
  if (Array.isArray(v)) return v.filter((x) => !isMarker(x)).length;
  if (isPlainObject(v)) return Object.keys(v).filter((k) => !MARKER_KEYS.includes(k)).length;
  return 0;
}

/** Record removed keys on an object, naming a few and counting the rest. */
function noteOmitted(node, keys) {
  const prior = Array.isArray(node._omitted) ? node._omitted : [];
  const named = prior.filter((k) => !/^\+\d+ more$/.test(k));
  const counted = prior.reduce((n, k) => {
    const m = /^\+(\d+) more$/.exec(k);
    return m ? n + Number(m[1]) : n;
  }, 0);
  const all = [...named, ...keys];
  const extra = counted + Math.max(0, all.length - MAX_OMITTED_NAMES);
  node._omitted = [...all.slice(0, MAX_OMITTED_NAMES), ...(extra ? [`+${extra} more`] : [])];
}

/** Keep an array's `_sampled` marker true after rows are removed from it. */
function noteRemovedRows(arr, before) {
  const at = arr.findIndex(isMarker);
  const total = at === -1 ? before : Number((/of (\d+) rows/.exec(arr[at]._sampled) || [])[1]) || before;
  if (at !== -1) arr.splice(at, 1);
  arr.push({ _sampled: `${arr.length} of ${total} rows` });
}

/**
 * Remove the members at `ats` from `node`, recording what went: object keys
 * are named under `_omitted`, array rows update `_sampled`. An array left
 * with no rows is emptied outright, so its parent can drop it by name.
 */
function removeMembers(node, ats) {
  if (!ats.length) return;
  if (Array.isArray(node)) {
    const before = memberCount(node);
    const drop = new Set(ats);
    const kept = node.filter((v, i) => !drop.has(i));
    node.length = 0;
    node.push(...kept);
    if (memberCount(node)) noteRemovedRows(node, before);
    else node.length = 0;
  } else {
    for (const k of ats) delete node[k];
    noteOmitted(node, ats);
  }
}

/**
 * Shed at least `excess` characters from `node`, in place.
 *
 * Three moves, in order of how much shape they keep:
 *  - one member alone outweighs the excess and has parts of its own: cut
 *    inside it, so a big table loses its widest cells rather than the object
 *    holding it losing the whole table;
 *  - covering the excess would take most of this level's members, and they
 *    have parts of their own: each gives up a share from inside, so thirty
 *    scenarios keep their names instead of twenty of them vanishing;
 *  - otherwise the largest members at this level go, biggest first, in one
 *    pass, so the small headline figures survive longest.
 * A member emptied from inside is then removed by name, never left behind as
 * an empty list the agent would read as "none".
 * Returns false when there is nothing left to remove.
 */
function shed(node, excess) {
  let members;
  if (Array.isArray(node)) {
    members = node.map((v, i) => ({ at: i, v })).filter((m) => !isMarker(m.v));
  } else if (isPlainObject(node)) {
    members = Object.keys(node).filter((k) => !MARKER_KEYS.includes(k)).map((k) => ({ at: k, v: node[k] }));
  } else {
    return false;
  }
  if (!members.length) return false;

  /* Measured once per pass: a comparator that serialised on every comparison
     would make a wide object quadratic. The key and punctuation count too. */
  for (const m of members) m.cost = size(m.v) + (typeof m.at === 'string' ? m.at.length + 4 : 1);
  members.sort((a, b) => b.cost - a.cost);

  const emptied = (touched) => removeMembers(node, touched.filter((m) => onlyMarkers(m.v)).map((m) => m.at));

  const [top] = members;
  if (top.cost > excess && memberCount(top.v) > 1 && shed(top.v, excess)) {
    emptied([top]);
    return true;
  }

  const removed = [];
  let freed = 0;
  for (const m of members) {
    if (freed >= excess && removed.length) break;
    removed.push(m);
    freed += m.cost;
  }

  const compound = members.filter((m) => memberCount(m.v) > 1);
  if (removed.length * 2 > members.length && compound.length > 1) {
    const share = Math.ceil(excess / compound.length);
    const touched = compound.filter((m) => shed(m.v, share));
    if (touched.length) {
      emptied(touched);
      return true;
    }
  }

  removeMembers(node, removed.map((m) => m.at));
  return true;
}

/** True when nothing but cut markers is left. */
function onlyMarkers(v) {
  return memberCount(v) === 0;
}

/**
 * Serialise a result for the report's second line: valid JSON, at most
 * `maxChars`, or '' — never a truncated fragment the agent would misread.
 *
 * In order, until it fits:
 *  1. prune (`__typename`, functions, `undefined`, cycles; floats to 4 dp) and
 *     sample every array longer than `maxRows`;
 *  2. sample tighter — half the rows each pass, down to two (first and last);
 *  3. clip long strings, which no amount of row sampling shrinks;
 *  4. shed the largest remaining members, naming them in `_omitted`, so the
 *     smallest facts — usually the headline — survive.
 * If only markers are left, or anything throws, the answer is '' and the
 * status line goes alone.
 */
export function compactForAgent(data, { maxChars = 6000, maxRows = 12 } = {}) {
  if (data === null || typeof data !== 'object') return '';
  try {
    const clean = prune(data, new WeakSet());
    if (clean === undefined || onlyMarkers(clean)) return '';

    let rows = Math.max(2, Math.floor(maxRows) || 12);
    let shaped = downsample(clean, rows);
    let json = JSON.stringify(shaped);

    while (json.length > maxChars && rows > 2) {
      rows = Math.max(2, Math.floor(rows / 2));
      shaped = downsample(clean, rows);
      json = JSON.stringify(shaped);
    }

    if (json.length > maxChars) {
      shaped = clipStrings(shaped, MAX_JSON_STRING);
      json = JSON.stringify(shaped);
    }

    for (let pass = 0; json.length > maxChars; pass++) {
      if (pass >= MAX_SHED_PASSES || !shed(shaped, json.length - maxChars) || onlyMarkers(shaped)) return '';
      json = JSON.stringify(shaped);
    }

    if (onlyMarkers(shaped)) return '';
    JSON.parse(json);
    return json;
  } catch (e) {
    return '';
  }
}
